import CreateButton from "../components/CreateButton"
import { useCallback, useEffect, useMemo, useState } from 'react'
import { Helmet, HelmetProvider } from 'react-helmet-async'
import { useParams } from 'react-router-dom'
import api from '../api'
import UserPlaceholder from '../assets/images/placeholders/user-placeholder.png'
import Card from '../components/cards/Card'
import Breadcrumb from '../components/Breadcrumb'
import { apiErrorText } from '../utils/apiError'
import Button from '../components/Button'
import Dialog from '../components/dialogs/Dialog'
import HeaderFooterLayout from '../components/layouts/HeaderFooterLayout'
import Select from '../components/Select'
import Skeleton from '../components/Skeleton'
import StudentProgress from '../components/StudentProgress'
import Table from '../components/Table'
import { formatDateV2, formatSeconds, formatTimeV2 } from '../utils'
import styles from './CompanyUser.module.css'

const CompanyUser = () => {
  const { studentId } = useParams()

  const [loading, setLoading] = useState(true)
  const [assigning, setAssigning] = useState(false)
  const [loadingProgresses, setLoadingProgresses] = useState(true)
  const [user, setUser] = useState(null)
  const [courses, setCourses] = useState([])
  const [progresses, setProgresses] = useState([])
  const [recentSessions, setRecentSessions] = useState([])
  const [error, setError] = useState(null)
  const [confirmCourse, setConfirmCourse] = useState(null)
  const [courseDialogOpen, setCourseDialogOpen] = useState(false)
  const [courseSearch, setCourseSearch] = useState('')
  const [selectedCourseOption, setSelectedCourseOption] = useState(null)
  const [studentProgressDetail, setStudentProgressDetail] = useState(null)

  const pageTitle = useMemo(() => {
    const fullName = `${user?.name || ''} ${user?.surname || ''}`.trim()
    return fullName || 'Dettagli Utente'
  }, [user?.name, user?.surname])

  const loadDetail = useCallback(async () => {
    if (!studentId) {
      setError('Utente non valido')
      setLoading(false)
      return
    }

    setLoading(true)
    setError(null)
    try {
      const response = await api.get(`/company/users/${studentId}`)
      setUser(response?.user || null)
      setCourses(response?.company_courses || [])
      setRecentSessions(response?.recent_sessions || [])
    } catch (e) {
      setError(apiErrorText(e, 'Impossibile caricare i dettagli utente'))
    }
    setLoading(false)
  }, [studentId])

  const loadProgresses = useCallback(async () => {
    if (!studentId) {
      setProgresses([])
      setLoadingProgresses(false)
      return
    }

    setLoadingProgresses(true)
    try {
      const response = await api.get(`/company/users/${studentId}/progresses`)
      setProgresses(response?.progresses || [])
    } catch (e) {
      setProgresses([])
      setError(apiErrorText(e, 'Impossibile caricare i progress utente'))
    }
    setLoadingProgresses(false)
  }, [studentId])

  useEffect(() => {
    loadDetail()
    loadProgresses()
  }, [loadDetail, loadProgresses])

  const assignCourse = useCallback(async () => {
    if (!confirmCourse?.course_id || assigning) {
      return
    }

    setAssigning(true)
    setError(null)
    try {
      await api.post(`/company/users/${studentId}/courses/${confirmCourse.course_id}`, {})
      setConfirmCourse(null)
      await Promise.all([loadDetail(), loadProgresses()])
    } catch (e) {
      setError(apiErrorText(e, 'Impossibile associare il corso'))
    }
    setAssigning(false)
  }, [assigning, confirmCourse, loadDetail, loadProgresses, studentId])

  const assignableCourseOptions = useMemo(() => {
    return courses
      .filter((course) => !course?.assigned)
      .map((course) => {
        const availableSlots = Number(course?.available_slots || 0)
        const maxUsers = Number(course?.max_users || 0)
        return {
          id: course.course_id,
          value: course.course_id,
          name: `${course.course_name} (${availableSlots}/${maxUsers} posti disponibili)`,
          course,
          availableSlots,
        }
      })
  }, [courses])

  const selectedCourse = selectedCourseOption?.course || null
  const selectedAvailableSlots = Number(selectedCourse?.available_slots || 0)

  const getStudentViewedLessonsValue = (student) => {
    if (!student?.modules || !Array.isArray(student.modules)) {
      return 0
    }
    return student.modules.reduce((total, module) => {
      const completedLessons = Array.isArray(module?.lessons)
        ? module.lessons.filter((lesson) => lesson?.mandatory !== false && lesson?.completed).length
        : 0
      return total + completedLessons
    }, 0)
  }

  const getStudentTotalLessonsValue = (student) => {
    if (!student?.modules || !Array.isArray(student.modules)) {
      return 0
    }
    return student.modules.reduce((total, module) => {
      return total + (Array.isArray(module?.lessons) ? module.lessons.filter((lesson) => lesson?.mandatory !== false).length : 0)
    }, 0)
  }

  const getStudentProgressValue = (student) => {
    if (!student) {
      return 0
    }
    if (student.edition_type === 'live') {
      const duration = Number(student.edition_duration) || 0
      const progress = Number(student.edition_seconds_progress) || 0
      if (duration <= 0) {
        return 0
      }
      const percentage = Math.round((progress * 100) / duration)
      return Number.isNaN(percentage) ? 0 : Math.max(0, Math.min(100, percentage))
    }

    const totalLessons = getStudentTotalLessonsValue(student)
    const viewedLessons = getStudentViewedLessonsValue(student)
    if (totalLessons <= 0) {
      return 0
    }

    const percentage = Math.round((viewedLessons / totalLessons) * 100)
    return Number.isNaN(percentage) ? 0 : percentage
  }

  const enrolledCourseRows = progresses.map((studentCourse) => ({
    ...studentCourse,
    id: `${studentCourse.id}-${studentCourse.course_id ?? 'course'}`,
    lessons: {
      value: getStudentViewedLessonsValue(studentCourse),
      type: 'number',
      component: <span>{`${getStudentViewedLessonsValue(studentCourse)} / ${getStudentTotalLessonsValue(studentCourse)}`}</span>,
    },
    progress: {
      value: getStudentProgressValue(studentCourse),
      type: 'number',
      component: <span>{`${getStudentProgressValue(studentCourse)}%`}</span>,
    },
  }))

  const sessionRows = useMemo(() => {
    return recentSessions.map((session) => ({
      ...session,
      started_at: session?.started_at,
      ended_at: session?.ended_at,
      duration: {
        value: Number(session?.duration || 0),
        type: 'number',
        component: <span>{formatSeconds(session?.duration || 0)}</span>,
      },
    }))
  }, [recentSessions])

  const dateTimeOptions = { day: 'numeric', month: 'long', year: 'numeric' }

  const formatDateTime = (date) => {
    if (!date) {
      return '-'
    }
    return `${formatDateV2(date, dateTimeOptions, 'it', true, true)}, ${formatTimeV2(date, { hour: '2-digit', minute: '2-digit', second: '2-digit' })}`
  }

  return (
    <HeaderFooterLayout>
      <HelmetProvider>
        <Helmet>
          <title>{pageTitle}</title>
        </Helmet>
      </HelmetProvider>
      <div className={styles.container}>
        <div className={styles.section}>
          <div className={styles.sectionInner}>
            <Breadcrumb items={[{ label: 'Utenti', to: '/users/students' }, { label: pageTitle }]} />

            {loading ? (
              <div className={styles.headerSkeleton}>
                <Skeleton height={30} width={'min(320px, 100%)'} borderRadius={8} />
                <Skeleton height={16} width={'min(260px, 100%)'} borderRadius={8} />
              </div>
            ) : (
              <div className={styles.userHeader}>
                <img className={styles.avatar} src={user?.picture || UserPlaceholder} alt='Foto utente' />
                <div>
                  <h1 className={styles.pageTitle}>{user ? `${user.name} ${user.surname}` : 'Dettaglio utente'}</h1>
                  {user?.email && <div className={styles.subtitle}>{user.email}</div>}
                </div>
              </div>
            )}

            {error && <div role="alert" className={styles.error}>{error}</div>}

            {!loading && (
              <div className={styles.detailSection}>
                <div className={styles.sectionTitle}>Dettagli Utente</div>
                <div className={styles.infoGrid}>
                  <div className={styles.infoCard}>
                    <div className={styles.infoLabel}>Creazione Utenza</div>
                    <div className={styles.infoValue}>{formatDateTime(user?.created_at)}</div>
                  </div>
                  <div className={styles.infoCard}>
                    <div className={styles.infoLabel}>Data Registrazione</div>
                    <div className={styles.infoValue}>{formatDateTime(user?.registered_at)}</div>
                  </div>
                  <div className={styles.infoCard}>
                    <div className={styles.infoLabel}>Ultimo Accesso</div>
                    <div className={styles.infoValue}>{formatDateTime(user?.last_access)}</div>
                  </div>
                </div>
              </div>
            )}

            <Card appearance="modern" integratedTable title="Corsi" actions={<CreateButton onClick={() => { setCourseSearch(''); setCourseDialogOpen(true) }}>Aggiungi corso</CreateButton>}>
              <Table
                integrated loadingN={4}
                loading={loading || loadingProgresses}
                columns={[
                  { key: 'course_name', label: 'CORSO', type: 'string' },
                  { key: 'lessons', label: 'LEZIONI', type: 'custom' },
                  { key: 'progress', label: 'PROGRESSO', type: 'custom' },
                  { key: 'registered_at', label: 'REGISTRATO IL', type: 'datetime' },
                  { key: 'last_access', label: 'ULTIMO ACCESSO', type: 'datetime' },
                ]}
                elements={enrolledCourseRows}
                defaultSort={{ key: 'course_name', direction: 'asc' }}
                onRowClick={(row) => setStudentProgressDetail(row)}
              />
              {!loading && !loadingProgresses && !error && enrolledCourseRows.length === 0 && <p className={styles.emptyState}>Nessun corso associato allo studente.</p>}
            </Card>

            <Card appearance="modern" integratedTable title="Sessioni recenti">
              <Table
                integrated loadingN={4}
                loading={loading}
                columns={[
                  { key: 'started_at', label: 'INIZIATA IL', type: 'datetime' },
                  { key: 'ended_at', label: 'TERMINATA IL', type: 'datetime' },
                  { key: 'duration', label: 'DURATA', type: 'custom' },
                ]}
                elements={sessionRows}
                defaultSort={{ key: 'started_at', direction: 'desc' }}
              />
              {!loading && !error && sessionRows.length === 0 && <p className={styles.emptyState}>Nessuna sessione recente.</p>}
            </Card>
          </div>
        </div>
      </div>

      <Dialog appearance="modern" open={!!confirmCourse} title="Conferma associazione corso"
        style={{ width: '94vw', maxWidth: '520px' }}
        onClose={() => { if (!assigning) setConfirmCourse(null) }}
        action={<div className={styles.confirmActions}>
          <Button appearance="subtle" disabled={assigning} onClick={() => setConfirmCourse(null)}>Annulla</Button>
          <Button style={{ borderRadius: 8 }} disabled={assigning} onClick={assignCourse}>{assigning ? 'Associazione...' : 'Conferma associazione'}</Button>
        </div>}
      >
        <p className={styles.sectionHint}>Confermando, l’utente verrà associato in modo definitivo al corso «{confirmCourse?.course_name}». Questa operazione utilizzerà uno dei posti disponibili e non sarà possibile tornare indietro.</p>
        {error && <div role="alert" className={styles.error}>{error}</div>}
      </Dialog>

      {studentProgressDetail && (
        <Dialog appearance="modern"
          title={`${studentProgressDetail.name} ${studentProgressDetail.surname}`}
          open={!!studentProgressDetail}
          onClose={() => setStudentProgressDetail(null)}
          style={{ width: '94vw', maxWidth: '1120px' }}
        >
          <StudentProgress modern student={studentProgressDetail} canSetLessonCompleted={false} />
        </Dialog>
      )}

      <Dialog appearance="modern"
        open={courseDialogOpen}
        title='Associa corso aziendale'
        onClose={() => {
          if (!assigning) {
            setCourseDialogOpen(false)
            setSelectedCourseOption(null)
          }
        }}
        style={{ width: '90vw', maxWidth: '640px' }}
      >
        <div className={styles.assignDialogBody}>
          <div className={styles.sectionHint}>Seleziona un corso aziendale da associare all'utente.</div>

          <Select appearance="modern" portal
            placeholder='Seleziona corso'
            options={assignableCourseOptions.filter(option => option.name.toLocaleLowerCase('it').includes(courseSearch.toLocaleLowerCase('it')))}
            onSearch={setCourseSearch}
            searchPlaceholder='Cerca corso'
            selected={selectedCourseOption}
            onSelect={(option) => {
              setSelectedCourseOption(option?.value ? option : null)
            }}
            nullable
            nullLabel='Nessun corso selezionato'
            disabled={loading || assigning || assignableCourseOptions.length === 0}
          />

          {selectedCourse && (
            <div className={styles.assignInfo}>
              Posti disponibili: {selectedAvailableSlots} su {selectedCourse?.max_users || 0}
            </div>
          )}

          {assignableCourseOptions.length === 0 && (
            <div className={styles.assignInfo}>Non ci sono corsi associabili disponibili.</div>
          )}

          <div className={styles.assignDialogActions}>
            <Button
              style={{ borderRadius: 8 }}
              disabled={!selectedCourse || selectedAvailableSlots <= 0 || assigning}
              onClick={() => {
                if (!selectedCourse) {
                  return
                }
                setCourseDialogOpen(false)
                setError(null)
                setConfirmCourse(selectedCourse)
                setSelectedCourseOption(null)
              }}
            >
              Associa corso
            </Button>
          </div>
        </div>
      </Dialog>
    </HeaderFooterLayout>
  )
}

export default CompanyUser
